import React from 'react';
import { Button } from '../ui/button';
import { X } from '@phosphor-icons/react';
import { backdropClick, stopBackdropPropagation } from '../../lib/modalBackdrop';

const EDIT_FIELDS = [
  { key: 'pod_code', label: 'Codice POD' },
  { key: 'customer_name', label: 'Cliente' },
  { key: 'customer_vat', label: 'P.IVA' },
  { key: 'customer_tax_code', label: 'Codice Fiscale' },
  { key: 'province', label: 'Provincia', max: 2, transform: v => v.toUpperCase() },
  { key: 'toponimo', label: 'Toponimo', placeholder: 'Via, Piazza...' },
  { key: 'indirizzo', label: 'Indirizzo' },
  { key: 'civico', label: 'Civico' },
  { key: 'cap', label: 'CAP', max: 5 },
  { key: 'comune', label: 'Comune' },
  { key: 'codice_istat', label: 'Codice ISTAT' },
];

export default function EditPodModal({ editPod, editForm, setEditForm, saving, onSave, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" {...backdropClick(onClose, saving)}>
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" {...stopBackdropPropagation} data-testid="edit-pod-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">Modifica POD: {editPod.pod_code}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={20}/></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {EDIT_FIELDS.map(f => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-slate-600 mb-1">{f.label}</label>
              <input value={editForm[f.key] || ''} maxLength={f.max}
                onChange={e => setEditForm({...editForm, [f.key]: f.transform ? f.transform(e.target.value) : e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" placeholder={f.placeholder} />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tipologia d'uso</label>
            <select value={editForm.usage_type || 'altri_usi'} onChange={e => setEditForm({...editForm, usage_type: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="edit-usage-type">
              <option value="altri_usi">Altri usi</option>
              <option value="domestico">Domestico</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Consumo Annuo kWh</label>
            <input type="number" step="0.01" value={editForm.annual_consumption_kwh || 0}
              onChange={e => setEditForm({...editForm, annual_consumption_kwh: parseFloat(e.target.value) || 0})}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="edit-consumption" />
          </div>
          {editForm.usage_type === 'domestico' && (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Potenza Impegnata (kW)</label>
                <input type="number" step="0.1" value={editForm.potenza_impegnata_kw ?? 3.0}
                  onChange={e => setEditForm({...editForm, potenza_impegnata_kw: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="edit-potenza" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Residenza Anagrafica</label>
                <select value={editForm.residenza_anagrafica === false ? 'false' : 'true'}
                  onChange={e => setEditForm({...editForm, residenza_anagrafica: e.target.value === 'true'})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="edit-residenza">
                  <option value="true">Residente (Prima Casa)</option>
                  <option value="false">Non Residente (Seconde Case)</option>
                </select>
              </div>
            </>
          )}
          {editForm.usage_type === 'altri_usi' && (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Processo Esente Art. 21 TUA</label>
                <select value={editForm.processo_esente_art21 || ''}
                  onChange={e => setEditForm({...editForm, processo_esente_art21: e.target.value || null})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="edit-processo-esente">
                  <option value="">Nessuna esenzione</option>
                  <option value="mineralogico">Processo Mineralogico</option>
                  <option value="elettrolitico_metallurgico">Processo Elettrolitico/Metallurgico</option>
                  <option value="riduzione_chimica">Riduzione Chimica</option>
                </select>
              </div>
              <div className="flex items-center gap-2 py-2">
                <input type="checkbox" id="illum_pubblica" checked={editForm.illuminazione_pubblica || false}
                  onChange={e => setEditForm({...editForm, illuminazione_pubblica: e.target.checked})}
                  className="rounded" data-testid="edit-illuminazione" />
                <label htmlFor="illum_pubblica" className="text-xs font-medium text-slate-600">Illuminazione Pubblica</label>
              </div>
            </>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Data Inizio</label>
            <input type="date" value={editForm.activation_date || ''}
              onChange={e => setEditForm({...editForm, activation_date: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" />
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mt-4 text-xs text-slate-600">
          <strong>Calcolo automatico TUA:</strong> L'accisa viene ricalcolata in base alla tipologia d'uso, potenza, residenza e consumo.
          Domestico residente fino a 3kW gode di esenzione 150 kWh/mese (con riassorbimento). Altri usi: scaglioni progressivi (0,0125 fino a 200.000 kWh/mese, 0,0075 oltre). Art.21 TUA: esenzione totale per processi specifici.
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Annulla</Button>
          <Button onClick={onSave} disabled={saving}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-sm" data-testid="save-pod-edit">
            {saving ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </div>
      </div>
    </div>
  );
}
